import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, Heart, ShoppingBag, User, Menu, X } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { useStore } from "@/context/StoreContext";
import { api, imgUrl, money } from "@/lib/api";

const NAV_LINKS = [
  { to: "/", label: "Home" },
  { to: "/shop", label: "Shop" },
  { to: "/collections", label: "Collections" },
  { to: "/lookbook", label: "Lookbook" },
  { to: "/about", label: "About" },
  { to: "/journal", label: "Journal" },
  { to: "/contact", label: "Contact" },
];

const SearchDialog = ({ open, setOpen }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    if (!query.trim()) return setResults([]);
    const t = setTimeout(() => {
      api.get("/products", { params: { search: query, limit: 6 } }).then(({ data }) => setResults(data));
    }, 250);
    return () => clearTimeout(t);
  }, [query]);

  const go = (slug) => {
    setOpen(false);
    setQuery("");
    navigate(`/product/${slug}`);
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="bg-[#0A0A0A] border-white/10 rounded-none max-w-xl p-0 top-[20%] translate-y-0">
        <div className="flex items-center gap-3 border-b border-white/10 px-5 py-4">
          <Search size={18} className="text-[#0FB5A3]" />
          <input
            autoFocus
            data-testid="search-input"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search the collection..."
            className="flex-1 bg-transparent outline-none text-white placeholder:text-white/30 text-sm tracking-wide"
          />
        </div>
        <div className="max-h-80 overflow-y-auto">
          {results.map((p) => (
            <button
              key={p.id}
              data-testid={`search-result-${p.slug}`}
              onClick={() => go(p.slug)}
              className="flex items-center gap-4 w-full px-5 py-3 hover:bg-white/5 transition-colors text-left"
            >
              <img src={imgUrl(p.images[0], 100)} alt={p.name} loading="lazy" decoding="async" className="w-12 h-14 object-cover" />
              <div className="flex-1">
                <p className="text-sm text-white font-display">{p.name}</p>
                <p className="text-xs text-white/40 uppercase tracking-wider">{p.category}</p>
              </div>
              <span className="text-sm text-[#0FB5A3]">{money(p.price)}</span>
            </button>
          ))}
          {query && results.length === 0 && (
            <p className="px-5 py-6 text-sm text-white/40">No pieces found for “{query}”.</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const Navbar = () => {
  const { cartCount, setCartOpen, wishlist } = useStore();
  const [searchOpen, setSearchOpen] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <>
      <div className="fixed top-0 inset-x-0 z-50">
        <div className="bg-[#6D28D9] text-white text-center py-1.5 px-4">
          <p className="text-[10px] tracking-[0.25em] uppercase" data-testid="announcement-bar">
            WE NOW US NEXT · Free shipping over $150 · Code WELCOME10
          </p>
        </div>
        <header
          className={`transition-[background-color,box-shadow,border-color] duration-500 ${
            scrolled ? "bg-black/80 backdrop-blur-2xl border-b border-white/10" : "bg-gradient-to-b from-black/70 to-transparent"
          }`}
        >
          <div className="max-w-[1600px] mx-auto px-5 lg:px-10 flex items-center justify-between h-16 lg:h-20">
            {/* Mobile menu */}
            <div className="lg:hidden">
              <Sheet open={menuOpen} onOpenChange={setMenuOpen}>
                <SheetTrigger asChild>
                  <button data-testid="mobile-menu-btn" aria-label="Menu" className="p-2 -ml-2 text-white">
                    <Menu size={22} strokeWidth={1.5} />
                  </button>
                </SheetTrigger>
                <SheetContent side="left" className="bg-black border-white/10 w-72 p-0">
                  <div className="p-6 border-b border-white/10">
                    <Logo size="sm" />
                  </div>
                  <nav className="flex flex-col p-6 gap-5">
                    {NAV_LINKS.map((l) => (
                      <NavLink
                        key={l.to}
                        to={l.to}
                        data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                        onClick={() => setMenuOpen(false)}
                        className="text-sm tracking-[0.2em] uppercase text-white/70 hover:text-[#0FB5A3] transition-colors"
                      >
                        {l.label}
                      </NavLink>
                    ))}
                    <NavLink to="/wishlist" onClick={() => setMenuOpen(false)} className="text-sm tracking-[0.2em] uppercase text-white/70 hover:text-[#0FB5A3] transition-colors">
                      Wishlist
                    </NavLink>
                    <NavLink to="/account" onClick={() => setMenuOpen(false)} className="text-sm tracking-[0.2em] uppercase text-white/70 hover:text-[#0FB5A3] transition-colors">
                      Account
                    </NavLink>
                  </nav>
                </SheetContent>
              </Sheet>
            </div>

            <Link to="/" data-testid="nav-logo-link" className="shrink-0">
              <Logo size="md" />
            </Link>

            <nav className="hidden lg:flex items-center gap-9">
              {NAV_LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`nav-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `link-757 text-[11px] tracking-[0.25em] uppercase transition-colors ${
                      isActive ? "text-[#0FB5A3]" : "text-white/70 hover:text-white"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
            </nav>

            <div className="flex items-center gap-1 sm:gap-2">
              <button
                data-testid="nav-search-btn"
                aria-label="Search"
                onClick={() => setSearchOpen(true)}
                className="p-2 text-white/80 hover:text-[#0FB5A3] transition-colors"
              >
                <Search size={19} strokeWidth={1.5} />
              </button>
              <Link
                to="/wishlist"
                data-testid="nav-wishlist-link"
                aria-label="Wishlist"
                className="p-2 text-white/80 hover:text-[#0FB5A3] transition-colors relative hidden sm:block"
              >
                <Heart size={19} strokeWidth={1.5} />
                {wishlist.length > 0 && (
                  <span className="absolute top-0.5 right-0.5 w-2 h-2 bg-[#6D28D9] rounded-full" />
                )}
              </Link>
              <Link
                to="/account"
                data-testid="nav-account-link"
                aria-label="Account"
                className="p-2 text-white/80 hover:text-[#0FB5A3] transition-colors hidden sm:block"
              >
                <User size={19} strokeWidth={1.5} />
              </Link>
              <button
                data-testid="nav-cart-btn"
                aria-label="Cart"
                onClick={() => setCartOpen(true)}
                className="p-2 text-white/80 hover:text-[#0FB5A3] transition-colors relative"
              >
                <ShoppingBag size={19} strokeWidth={1.5} />
                {cartCount > 0 && (
                  <span
                    data-testid="cart-count-badge"
                    className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 bg-[#0FB5A3] text-black text-[10px] font-semibold flex items-center justify-center rounded-full"
                  >
                    {cartCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </header>
      </div>
      <SearchDialog open={searchOpen} setOpen={setSearchOpen} />
    </>
  );
};
